import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Traduções
const translations = {
  en: {
    // Header
    pricing: 'Pricing',
    login: 'Login',
    startFreeTrial: 'Start Free Trial',
    
    // Landing Page
    heroTitle: 'Your Business on Autopilot',
    heroSubtitle: 'AI assistants that serve your customers, sell your products and manage traffic campaigns 24/7, automatically.',
    freeTrialBenefits: '14-day free trial • No credit card required • 5-minute setup',
    alreadyHaveAccount: 'Already have an account',
    
    // Features
    howItTransforms: 'How BIZREP Transforms Your Business',
    personalizedAssistant: 'Personalized AI Assistant',
    customerService247: '24/7 Customer Service',
    trafficManagement: 'AI Traffic Management',
    
    // How it Works
    howItWorks: 'How It Works',
    threeSteps: 'In just 3 steps, your business will be running on autopilot',
    registerBusiness: 'Register Your Business',
    registerBusinessDesc: 'Provide your website and social media. Our AI automatically analyzes your business, products and communication tone.',
    connectChannels: 'Connect Your Channels',
    connectChannelsDesc: 'We connect your WhatsApp, Instagram and Facebook. Your personalized assistant is ready to serve customers.',
    setBudget: 'Set Your Budget',
    setBudgetDesc: 'Configure your marketing budget. Our AI creates and manages campaigns to bring new customers automatically.',
    
    // Products and Services
    forProductsServices: 'For Products and Services',
    productsAndServices: 'For Products and Services',
    businessModel: 'Whether selling physical products or providing services, BIZREP adapts perfectly to your business model.',
    productSales: 'Product Sales',
    productSalesDesc: 'Synchronized catalog, intelligent recommendations and direct checkout in chat.',
    leadQualification: 'Lead Qualification',
    leadQualificationDesc: 'For services, the assistant qualifies leads and schedules meetings automatically.',
    timeSavings: 'Time Savings',
    timeSavingsDesc: 'Stop answering the same questions. Focus on what really matters.',
    
    // Results
    realResults: 'Real Results',
    increaseConversions: 'Increase in conversions',
    nonStopService: 'Non-stop service',
    reductionResponseTime: 'Reduction in response time',
    
    // CTA
    readyToAutomate: 'Ready to Automate Your Business?',
    joinCompanies: 'Join hundreds of companies that have already transformed their results with BIZREP.',
    noCommitment: 'No commitment • Cancel anytime',
    cancelAnytime: 'Cancel anytime',
    
    // Login Page
    welcomeBack: 'Welcome back',
    signInToAccount: 'Sign in to your account to access your dashboard',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign In',
    signingIn: 'Signing in...',
    dontHaveAccount: "Don't have an account?",
    signUpFree: 'Sign up for free',
    forgotPassword: 'Forgot your password?',
    demoAccount: 'Demo Account:',
    backToWebsite: '← Back to website',
    
    // Register Page
    createFreeAccount: 'Create your free account',
    setupAssistant: 'Set up your AI assistant in less than 5 minutes',
    businessName: 'Business Name',
    businessType: 'Business Type',
    sellProducts: 'Sell Products',
    provideServices: 'Provide Services',
    companyWebsite: 'Company Website',
    instagram: 'Instagram',
    facebook: 'Facebook',
    accountInformation: 'Account Information',
    confirmPassword: 'Confirm Password',
    agreeTerms: 'I agree to the Terms of Use and Privacy Policy',
    termsOfUse: 'Terms of Use',
    privacyPolicy: 'Privacy Policy',
    createAccount: 'Create Free Account',
    myCompany: 'My Company',
    myInstagram: '@myinstagram',
    myFacebook: 'facebook.com/mypage',
    yourEmail: 'your@email.com',
    minimumCharacters: 'Minimum 6 characters',
    repeatPassword: 'Repeat password',
    aiAnalyzeWebsite: 'Our AI will analyze your website to understand your business',
    creatingAccount: 'Creating account...',
    passwordsDontMatch: 'Passwords do not match',
    passwordMinLength: 'Password must be at least 6 characters',
    
    // Dashboard
    dashboard: 'Dashboard',
    products: 'Products',
    conversations: 'Conversations',
    campaigns: 'Campaigns',
    agentConfig: 'Agent',
    admin: 'Admin',
    hello: 'Hello',
    businessSummary: 'Here is a summary of your business today',
    totalConversations: 'Total Conversations',
    activeConversations: 'Active Conversations',
    activeCampaigns: 'Active Campaigns',
    conversationsAndSales: 'Conversations and Sales',
    lastMonths: 'Last 6 months',
    quickActions: 'Quick Actions',
    manageProducts: 'Manage Products',
    viewConversations: 'View Conversations',
    aiAssistantStatus: 'AI Assistant Status',
    active: 'Active',
    responseTime: 'Response Time',
    messagesToday: 'Messages Today',
    conversionsToday: 'Conversions Today',
    synchronize: 'Synchronize',
    
    // Header/Auth
    settings: 'Settings',
    logout: 'Logout',
    
    // Footer
    footerDescription: 'Transform your business with AI assistants that serve your customers, sell your products and manage traffic campaigns automatically.',
    copyright: '© 2024 BIZREP. All rights reserved.',
    developedBy: 'Developed by',
    product: 'Product',
    features: 'Features',
    freeTrial: 'Free Trial',
    support: 'Support',
    contact: 'Contact',
    helpCenter: 'Help Center',
    
    // Pricing Page
    simpleTransparentPricing: 'Simple, Transparent Pricing',
    choosePerfectPlan: 'Choose the perfect plan for your business. Start with AI agents and upgrade to full automation when you\'re ready.',
    monthly: 'Monthly',
    yearly: 'Yearly',
    save17: 'Save 17%',
    mostPopular: 'Most Popular',
    agents: 'AGENTS',
    agentsDescription: 'AI Agent Creation & Management',
    full: 'FULL',
    fullDescription: 'Complete Business Automation',
    month: 'month',
    year: 'year',
    getStarted: 'Get Started',
    customAIAgent: 'Custom AI Agent Creation',
    websiteSocialAnalysis: 'Website & Social Media Analysis',
    productCatalogSync: 'Product Catalog Synchronization',
    whatsappIntegration: 'WhatsApp Integration',
    instagramFacebookConnect: 'Instagram & Facebook Connect',
    basicAnalytics: 'Basic Analytics Dashboard',
    emailSupport: 'Email Support',
    unlimitedProfiles: 'Unlimited Business Profiles',
    everythingInAgents: 'Everything in AGENTS',
    aiTrafficManager: 'AI Traffic Manager',
    googleAdsAutomation: 'Google Ads Automation',
    metaAdsManagement: 'Meta Ads Management',
    campaignOptimization: 'Campaign Optimization',
    advancedAnalytics: 'Advanced Analytics',
    leadQualificationSystem: 'Lead Qualification System',
    prioritySupport: 'Priority Support',
    customIntegrations: 'Custom Integrations',
    messageVolumePricing: 'Message Volume Pricing',
    payOnlyForMessages: 'Pay only for the messages your AI agents handle. Choose the package that fits your business volume.',
    upTo1000Messages: 'Up to 1,000 messages',
    perfectSmallBusiness: 'Perfect for small businesses',
    selectPackage: 'Select Package',
    growingBusinesses: 'Growing businesses',
    establishedCompanies: 'Established companies',
    highVolumeOperations: 'High-volume operations',
    enterpriseLevel: 'Enterprise level',
    payPerMessage: 'Pay per message',
    frequentlyAskedQuestions: 'Frequently Asked Questions',
    whatsIncludedAgents: 'What\'s included in the AGENTS plan?',
    agentsIncludesDescription: 'The AGENTS plan includes AI agent creation, social media analysis, product catalog sync, and basic integrations. Perfect for businesses starting with AI automation.',
    howTrafficManagerWorks: 'How does the traffic manager work?',
    trafficManagerDescription: 'Our AI traffic manager automatically creates, optimizes, and manages your Google and Meta ad campaigns, adjusting budgets and targeting for maximum ROI.',
    canChangeMessagePackage: 'Can I change my message package?',
    changePackageDescription: 'Yes, you can upgrade or downgrade your message package at any time. Changes take effect at the start of your next billing cycle.',
    isThereFreeTrial: 'Is there a free trial?',
    freeTrialDescription: 'Yes! We offer a 14-day free trial with up to 100 messages included. No credit card required to start.',
    enterpriseSolution: 'Enterprise Solution',
    enterpriseDescription: 'Need a custom solution for your large business? Contact us for personalized pricing and dedicated support.',
    contactUs: 'Contact Us',
    customQuote: 'Custom Quote',
    customPricing: 'Custom pricing for enterprise',
    above5000: 'Above 5,000',
    
    // Missing translations
    payOnlyMessages: 'Pay only for the messages your AI agents handle. Choose the package that fits your business volume.',
    agentsIncludesDesc: 'The AGENTS plan includes AI agent creation, social media analysis, product catalog sync, and basic integrations. Perfect for businesses starting with AI automation.',
    trafficManagerDesc: 'Our AI traffic manager automatically creates, optimizes, and manages your Google and Meta ad campaigns, adjusting budgets and targeting for maximum ROI.',
    changePackageDesc: 'Yes, you can upgrade or downgrade your message package at any time. Changes take effect at the start of your next billing cycle.',
    freeTrialDesc: 'Yes! We offer a 14-day free trial with up to 100 messages included. No credit card required to start.',
    readyToAutomateBusiness: 'Ready to Automate Your Business?',
    joinThousandsBusinesses: 'Join thousands of businesses worldwide that have transformed their customer service and sales with BIZREP.',
    startFreeTrialBtn: 'Start Free Trial',
    upTo1000: 'Up to 1,000',
    messages: 'messages'
  },
  pt: {
    // Header
    pricing: 'Preços',
    login: 'Entrar',
    startFreeTrial: 'Teste Grátis',
    
    // Landing Page
    heroTitle: 'Seu Negócio no Piloto Automático',
    heroSubtitle: 'Assistentes de IA que atendem seus clientes, vendem seus produtos e gerenciam campanhas de tráfego 24/7, automaticamente.',
    freeTrialBenefits: '14 dias grátis • Sem cartão de crédito • Configuração em 5 minutos',
    alreadyHaveAccount: 'Já tem uma conta',
    
    // Features
    howItTransforms: 'Como o BIZREP Transforma Seu Negócio',
    personalizedAssistant: 'Assistente IA Personalizado',
    customerService247: 'Atendimento 24/7',
    trafficManagement: 'Gestão de Tráfego IA',
    
    // How it Works
    howItWorks: 'Como Funciona',
    threeSteps: 'Em apenas 3 passos, seu negócio estará funcionando no piloto automático',
    registerBusiness: 'Cadastre Seu Negócio',
    registerBusinessDesc: 'Forneça seu site e redes sociais. Nossa IA analisa automaticamente seu negócio, produtos e tom de comunicação.',
    connectChannels: 'Conecte Seus Canais',
    connectChannelsDesc: 'Conectamos seu WhatsApp, Instagram e Facebook. Seu assistente personalizado está pronto para atender clientes.',
    setBudget: 'Defina Seu Orçamento',
    setBudgetDesc: 'Configure seu orçamento de marketing. Nossa IA cria e gerencia campanhas para trazer novos clientes automaticamente.',
    
    // Products and Services
    forProductsServices: 'Para Produtos e Serviços',
    productsAndServices: 'Para Produtos e Serviços',
    businessModel: 'Seja vendendo produtos físicos ou prestando serviços, o BIZREP se adapta perfeitamente ao seu modelo de negócio.',
    productSales: 'Vendas de Produtos',
    productSalesDesc: 'Catálogo sincronizado, recomendações inteligentes e checkout direto no chat.',
    leadQualification: 'Qualificação de Leads',
    leadQualificationDesc: 'Para serviços, o assistente qualifica leads e agenda reuniões automaticamente.',
    timeSavings: 'Economia de Tempo',
    timeSavingsDesc: 'Pare de responder as mesmas perguntas. Foque no que realmente importa.',
    
    // Results
    realResults: 'Resultados Reais',
    increaseConversions: 'Aumento nas conversões',
    nonStopService: 'Atendimento ininterrupto',
    reductionResponseTime: 'Redução no tempo de resposta',
    
    // CTA
    readyToAutomate: 'Pronto para Automatizar seu Negócio?',
    joinCompanies: 'Junte-se a centenas de empresas que já transformaram seus resultados com o BIZREP.',
    noCommitment: 'Sem compromisso • Cancele quando quiser',
    cancelAnytime: 'Cancele a qualquer momento',
    
    // Login Page
    welcomeBack: 'Bem-vindo de volta',
    signInToAccount: 'Entre na sua conta para acessar seu painel',
    email: 'E-mail',
    password: 'Senha',
    signIn: 'Entrar',
    signingIn: 'Entrando...',
    dontHaveAccount: 'Não tem uma conta?',
    signUpFree: 'Cadastre-se grátis',
    forgotPassword: 'Esqueceu sua senha?',
    demoAccount: 'Conta de demonstração:',
    backToWebsite: '← Voltar para o site',
    
    // Register Page
    createFreeAccount: 'Crie sua conta gratuita',
    setupAssistant: 'Configure seu assistente IA em menos de 5 minutos',
    businessName: 'Nome do Negócio',
    businessType: 'Tipo de Negócio',
    sellProducts: 'Vendo Produtos',
    provideServices: 'Presto Serviços',
    companyWebsite: 'Site da Empresa',
    instagram: 'Instagram',
    facebook: 'Facebook',
    accountInformation: 'Informações da Conta',
    confirmPassword: 'Confirmar Senha',
    agreeTerms: 'Concordo com os Termos de Uso e Política de Privacidade',
    termsOfUse: 'Termos de Uso',
    privacyPolicy: 'Política de Privacidade',
    createAccount: 'Criar Conta Gratuita',
    myCompany: 'Minha Empresa',
    myInstagram: '@meuinstagram',
    myFacebook: 'facebook.com/minhapagina',
    yourEmail: 'seu@email.com',
    minimumCharacters: 'Mínimo 6 caracteres',
    repeatPassword: 'Repetir senha',
    aiAnalyzeWebsite: 'Nossa IA analisará seu site para entender seu negócio',
    creatingAccount: 'Criando conta...',
    passwordsDontMatch: 'As senhas não coincidem',
    passwordMinLength: 'A senha deve ter pelo menos 6 caracteres',
    
    // Dashboard
    dashboard: 'Painel',
    products: 'Produtos',
    conversations: 'Conversas',
    campaigns: 'Campanhas',
    agentConfig: 'Agente',
    admin: 'Admin',
    hello: 'Olá',
    businessSummary: 'Aqui está um resumo do seu negócio hoje',
    totalConversations: 'Total de Conversas',
    activeConversations: 'Conversas Ativas',
    activeCampaigns: 'Campanhas Ativas',
    conversationsAndSales: 'Conversas e Vendas',
    lastMonths: 'Últimos 6 meses',
    quickActions: 'Ações Rápidas',
    manageProducts: 'Gerenciar Produtos',
    viewConversations: 'Ver Conversas',
    aiAssistantStatus: 'Status do Assistente IA',
    active: 'Ativo',
    responseTime: 'Tempo de Resposta',
    messagesToday: 'Mensagens Hoje',
    conversionsToday: 'Conversões Hoje',
    synchronize: 'Sincronizar',
    
    // Header/Auth
    settings: 'Configurações',
    logout: 'Sair',
    
    // Footer
    footerDescription: 'Transforme seu negócio com assistentes de IA que atendem seus clientes, vendem seus produtos e gerenciam campanhas de tráfego automaticamente.',
    copyright: '© 2024 BIZREP. Todos os direitos reservados.',
    developedBy: 'Desenvolvido por',
    product: 'Produto',
    features: 'Funcionalidades',
    freeTrial: 'Teste Grátis',
    support: 'Suporte',
    contact: 'Contato',
    helpCenter: 'Central de Ajuda',
    
    // Pricing Page
    simpleTransparentPricing: 'Preços Simples e Transparentes',
    choosePerfectPlan: 'Escolha o plano perfeito para seu negócio. Comece com agentes de IA e evolua para automação completa quando estiver pronto.',
    monthly: 'Mensal',
    yearly: 'Anual',
    save17: 'Economize 17%',
    mostPopular: 'Mais Popular',
    agents: 'AGENTES',
    agentsDescription: 'Criação e Gestão de Agentes IA',
    full: 'COMPLETO',
    fullDescription: 'Automação Completa de Negócios',
    month: 'mês',
    year: 'ano',
    getStarted: 'Começar',
    customAIAgent: 'Criação de Agente IA Personalizado',
    websiteSocialAnalysis: 'Análise de Site e Redes Sociais',
    productCatalogSync: 'Sincronização de Catálogo de Produtos',
    whatsappIntegration: 'Integração WhatsApp',
    instagramFacebookConnect: 'Conexão Instagram e Facebook',
    basicAnalytics: 'Painel de Análises Básico',
    emailSupport: 'Suporte por E-mail',
    unlimitedProfiles: 'Perfis de Negócio Ilimitados',
    everythingInAgents: 'Tudo do AGENTES',
    aiTrafficManager: 'Gestor de Tráfego IA',
    googleAdsAutomation: 'Automação Google Ads',
    metaAdsManagement: 'Gestão Meta Ads',
    campaignOptimization: 'Otimização de Campanhas',
    advancedAnalytics: 'Análises Avançadas',
    leadQualificationSystem: 'Sistema de Qualificação de Leads',
    prioritySupport: 'Suporte Prioritário',
    customIntegrations: 'Integrações Personalizadas',
    messageVolumePricing: 'Preços por Volume de Mensagens',
    payOnlyForMessages: 'Pague apenas pelas mensagens que seus agentes IA processam. Escolha o pacote que se adequa ao volume do seu negócio.',
    upTo1000Messages: 'Até 1.000 mensagens',
    perfectSmallBusiness: 'Perfeito para pequenos negócios',
    selectPackage: 'Selecionar Pacote',
    growingBusinesses: 'Negócios em crescimento',
    establishedCompanies: 'Empresas estabelecidas',
    highVolumeOperations: 'Operações de alto volume',
    enterpriseLevel: 'Nível empresarial',
    payPerMessage: 'Pague por mensagem',
    frequentlyAskedQuestions: 'Perguntas Frequentes',
    whatsIncludedAgents: 'O que está incluído no plano AGENTES?',
    agentsIncludesDescription: 'O plano AGENTES inclui criação de agente IA, análise de redes sociais, sincronização de catálogo de produtos e integrações básicas. Perfeito para negócios iniciando com automação IA.',
    howTrafficManagerWorks: 'Como funciona o gestor de tráfego?',
    trafficManagerDescription: 'Nosso gestor de tráfego IA cria, otimiza e gerencia automaticamente suas campanhas do Google e Meta, ajustando orçamentos e segmentação para máximo ROI.',
    canChangeMessagePackage: 'Posso alterar meu pacote de mensagens?',
    changePackageDescription: 'Sim, você pode fazer upgrade ou downgrade do seu pacote de mensagens a qualquer momento. As alterações entram em vigor no início do próximo ciclo de cobrança.',
    isThereFreeTrial: 'Existe teste gratuito?',
    freeTrialDescription: 'Sim! Oferecemos um teste gratuito de 14 dias com até 100 mensagens incluídas. Não é necessário cartão de crédito para começar.',
    enterpriseSolution: 'Solução Empresarial',
    enterpriseDescription: 'Precisa de uma solução personalizada para sua grande empresa? Entre em contato para preços personalizados e suporte dedicado.',
    contactUs: 'Entre em Contato',
    customQuote: 'Cotação Personalizada',
    customPricing: 'Preços personalizados para empresas',
    above5000: 'Acima de 5.000',
    upTo1000: 'Até 1.000',
    messages: 'mensagens',
    
    // Missing translations
    payOnlyMessages: 'Pague apenas pelas mensagens que seus agentes IA processam. Escolha o pacote que se adequa ao volume do seu negócio.',
    agentsIncludesDesc: 'O plano AGENTES inclui criação de agente IA, análise de redes sociais, sincronização de catálogo de produtos e integrações básicas. Perfeito para negócios iniciando com automação IA.',
    trafficManagerDesc: 'Nosso gestor de tráfego IA cria, otimiza e gerencia automaticamente suas campanhas do Google e Meta, ajustando orçamentos e segmentação para máximo ROI.',
    changePackageDesc: 'Sim, você pode fazer upgrade ou downgrade do seu pacote de mensagens a qualquer momento. As alterações entram em vigor no início do próximo ciclo de cobrança.',
    freeTrialDesc: 'Sim! Oferecemos um teste gratuito de 14 dias com até 100 mensagens incluídas. Não é necessário cartão de crédito para começar.',
    readyToAutomateBusiness: 'Pronto para Automatizar seu Negócio?',
    joinThousandsBusinesses: 'Junte-se a milhares de empresas no mundo todo que transformaram seu atendimento ao cliente e vendas com o BIZREP.',
    startFreeTrialBtn: 'Teste Grátis'
  },
  es: {
    // Header
    pricing: 'Precios',
    login: 'Iniciar Sesión',
    startFreeTrial: 'Prueba Gratis',
    
    // Hero Section
    heroTitle: 'Tu Negocio en Piloto Automático',
    heroSubtitle: 'Asistentes de IA que atienden a tus clientes, venden tus productos y gestionan campañas de tráfico 24/7, automáticamente.',
    freeTrialBenefits: '14 días gratis • Sin tarjeta de crédito • Configuración en 5 minutos',
    alreadyHaveAccount: 'Ya tienes una cuenta',
    
    // Features
    howItTransforms: 'Cómo BIZREP Transforma Tu Negocio',
    personalizedAssistant: 'Asistente IA Personalizado',
    customerService247: 'Atención 24/7',
    trafficManagement: 'Gestión de Tráfico IA',
    
    // How it Works
    howItWorks: 'Cómo Funciona',
    threeSteps: 'En solo 3 pasos, tu negocio estará funcionando en piloto automático',
    registerBusiness: 'Registra Tu Negocio',
    registerBusinessDesc: 'Proporciona tu sitio web y redes sociales. Nuestra IA analiza automáticamente tu negocio, productos y tono de comunicación.',
    connectChannels: 'Conecta Tus Canales',
    connectChannelsDesc: 'Conectamos tu WhatsApp, Instagram y Facebook. Tu asistente personalizado está listo para atender clientes.',
    setBudget: 'Define Tu Presupuesto',
    setBudgetDesc: 'Configura tu presupuesto de marketing. Nuestra IA crea y gestiona campañas para traer nuevos clientes automáticamente.',
    
    // Products and Services
    forProductsServices: 'Para Productos y Servicios',
    productsAndServices: 'Para Productos y Servicios',
    businessModel: 'Ya sea vendiendo productos físicos o prestando servicios, BIZREP se adapta perfectamente a tu modelo de negocio.',
    productSales: 'Ventas de Productos',
    productSalesDesc: 'Catálogo sincronizado, recomendaciones inteligentes y checkout directo en el chat.',
    leadQualification: 'Calificación de Leads',
    leadQualificationDesc: 'Para servicios, el asistente califica leads y agenda reuniones automáticamente.',
    timeSavings: 'Ahorro de Tiempo',
    timeSavingsDesc: 'Deja de responder las mismas preguntas. Enfócate en lo que realmente importa.',
    
    // Results
    realResults: 'Resultados Reales',
    increaseConversions: 'Aumento en conversiones',
    nonStopService: 'Atención ininterrumpida',
    reductionResponseTime: 'Reducción en tiempo de respuesta',
    
    // CTA
    readyToAutomate: '¿Listo para Automatizar tu Negocio?',
    joinCompanies: 'Únete a cientos de empresas que ya han transformado sus resultados con BIZREP.',
    noCommitment: 'Sin compromiso • Cancela cuando quieras',
    cancelAnytime: 'Cancela en cualquier momento',
    
    // Login Page
    welcomeBack: 'Bienvenido de vuelta',
    signInToAccount: 'Inicia sesión en tu cuenta para acceder a tu panel',
    email: 'Correo electrónico',
    password: 'Contraseña',
    signIn: 'Iniciar Sesión',
    signingIn: 'Iniciando sesión...',
    dontHaveAccount: '¿No tienes una cuenta?',
    signUpFree: 'Regístrate gratis',
    forgotPassword: '¿Olvidaste tu contraseña?',
    demoAccount: 'Cuenta de demostración:',
    backToWebsite: '← Volver al sitio web',
    
    // Register Page
    createFreeAccount: 'Crea tu cuenta gratuita',
    setupAssistant: 'Configura tu asistente IA en menos de 5 minutos',
    businessName: 'Nombre del Negocio',
    businessType: 'Tipo de Negocio',
    sellProducts: 'Vender Productos',
    provideServices: 'Prestar Servicios',
    companyWebsite: 'Sitio Web de la Empresa',
    instagram: 'Instagram',
    facebook: 'Facebook',
    accountInformation: 'Información de la Cuenta',
    confirmPassword: 'Confirmar Contraseña',
    agreeTerms: 'Acepto los Términos de Uso y Política de Privacidad',
    termsOfUse: 'Términos de Uso',
    privacyPolicy: 'Política de Privacidad',
    createAccount: 'Crear Cuenta Gratuita',
    myCompany: 'Mi Empresa',
    myInstagram: '@miinstagram',
    myFacebook: 'facebook.com/mipagina',
    yourEmail: 'tu@email.com',
    minimumCharacters: 'Mínimo 6 caracteres',
    repeatPassword: 'Repetir contraseña',
    aiAnalyzeWebsite: 'Nuestra IA analizará tu sitio web para entender tu negocio',
    creatingAccount: 'Creando cuenta...',
    passwordsDontMatch: 'Las contraseñas no coinciden',
    passwordMinLength: 'La contraseña debe tener al menos 6 caracteres',
    
    // Dashboard
    dashboard: 'Panel',
    products: 'Productos',
    conversations: 'Conversaciones',
    campaigns: 'Campañas',
    agentConfig: 'Agente',
    admin: 'Admin',
    hello: 'Hola',
    businessSummary: 'Aquí tienes un resumen de tu negocio hoy',
    totalConversations: 'Total de Conversaciones',
    activeConversations: 'Conversaciones Activas',
    activeCampaigns: 'Campañas Activas',
    conversationsAndSales: 'Conversaciones y Ventas',
    lastMonths: 'Últimos 6 meses',
    quickActions: 'Acciones Rápidas',
    manageProducts: 'Gestionar Productos',
    viewConversations: 'Ver Conversaciones',
    aiAssistantStatus: 'Estado del Asistente IA',
    active: 'Activo',
    responseTime: 'Tiempo de Respuesta',
    messagesToday: 'Mensajes Hoy',
    conversionsToday: 'Conversiones Hoy',
    synchronize: 'Sincronizar',
    
    // Header/Auth
    settings: 'Configuración',
    logout: 'Cerrar Sesión',
    
    // Footer
    footerDescription: 'Transforma tu negocio con asistentes de IA que atienden a tus clientes, venden tus productos y gestionan campañas de tráfico automáticamente.',
    copyright: '© 2024 BIZREP. Todos los derechos reservados.',
    developedBy: 'Desarrollado por',
    product: 'Producto',
    features: 'Funcionalidades',
    freeTrial: 'Prueba Gratis',
    support: 'Soporte',
    contact: 'Contacto',
    helpCenter: 'Centro de Ayuda',
    
    // Pricing Page
    simpleTransparentPricing: 'Precios Simples y Transparentes',
    choosePerfectPlan: 'Elige el plan perfecto para tu negocio. Comienza con agentes de IA y evoluciona hacia la automatización completa cuando estés listo.',
    monthly: 'Mensual',
    yearly: 'Anual',
    save17: 'Ahorra 17%',
    mostPopular: 'Más Popular',
    agents: 'AGENTES',
    agentsDescription: 'Creación y Gestión de Agentes IA',
    full: 'COMPLETO',
    fullDescription: 'Automatización Completa de Negocios',
    month: 'mes',
    year: 'año',
    getStarted: 'Comenzar',
    customAIAgent: 'Creación de Agente IA Personalizado',
    websiteSocialAnalysis: 'Análisis de Sitio Web y Redes Sociales',
    productCatalogSync: 'Sincronización de Catálogo de Productos',
    whatsappIntegration: 'Integración WhatsApp',
    instagramFacebookConnect: 'Conexión Instagram y Facebook',
    basicAnalytics: 'Panel de Análisis Básico',
    emailSupport: 'Soporte por Correo Electrónico',
    unlimitedProfiles: 'Perfiles de Negocio Ilimitados',
    everythingInAgents: 'Todo de AGENTES',
    aiTrafficManager: 'Gestor de Tráfico IA',
    googleAdsAutomation: 'Automatización Google Ads',
    metaAdsManagement: 'Gestión Meta Ads',
    campaignOptimization: 'Optimización de Campañas',
    advancedAnalytics: 'Análisis Avanzados',
    leadQualificationSystem: 'Sistema de Calificación de Leads',
    prioritySupport: 'Soporte Prioritario',
    customIntegrations: 'Integraciones Personalizadas',
    messageVolumePricing: 'Precios por Volumen de Mensajes',
    payOnlyForMessages: 'Paga solo por los mensajes que manejan tus agentes IA. Elige el paquete que se adapte al volumen de tu negocio.',
    upTo1000Messages: 'Hasta 1.000 mensajes',
    perfectSmallBusiness: 'Perfecto para pequeños negocios',
    selectPackage: 'Seleccionar Paquete',
    growingBusinesses: 'Negocios en crecimiento',
    establishedCompanies: 'Empresas establecidas',
    highVolumeOperations: 'Operaciones de alto volumen',
    enterpriseLevel: 'Nivel empresarial',
    payPerMessage: 'Pagar por mensaje',
    frequentlyAskedQuestions: 'Preguntas Frecuentes',
    whatsIncludedAgents: '¿Qué está incluido en el plan AGENTES?',
    agentsIncludesDescription: 'El plan AGENTES incluye creación de agente IA, análisis de redes sociales, sincronización de catálogo de productos e integraciones básicas. Perfecto para negocios que comienzan con automatización IA.',
    howTrafficManagerWorks: '¿Cómo funciona el gestor de tráfico?',
    trafficManagerDescription: 'Nuestro gestor de tráfico IA crea, optimiza y gestiona automáticamente tus campañas de Google y Meta, ajustando presupuestos y segmentación para máximo ROI.',
    canChangeMessagePackage: '¿Puedo cambiar mi paquete de mensajes?',
    changePackageDescription: 'Sí, puedes actualizar o reducir tu paquete de mensajes en cualquier momento. Los cambios entran en vigor al inicio de tu próximo ciclo de facturación.',
    isThereFreeTrial: '¿Hay una prueba gratuita?',
    freeTrialDescription: '¡Sí! Ofrecemos una prueba gratuita de 14 días con hasta 100 mensajes incluidos. No se requiere tarjeta de crédito para comenzar.',
    enterpriseSolution: 'Solución Empresarial',
    enterpriseDescription: '¿Necesitas una solución personalizada para tu gran empresa? Contáctanos para precios personalizados y soporte dedicado.',
    contactUs: 'Contáctanos',
    customQuote: 'Cotización Personalizada',
    customPricing: 'Precios personalizados para empresas',
    above5000: 'Más de 5.000',
    upTo1000: 'Hasta 1.000',
    messages: 'mensajes',
    
    // Missing translations
    payOnlyMessages: 'Paga solo por los mensajes que procesan tus agentes IA. Elige el paquete que se adapte al volumen de tu negocio.',
    agentsIncludesDesc: 'El plan AGENTES incluye creación de agente IA, análisis de redes sociales, sincronización de catálogo de productos e integraciones básicas. Perfecto para negocios que comienzan con automatización IA.',
    trafficManagerDesc: 'Nuestro gestor de tráfico IA crea, optimiza y gestiona automáticamente tus campañas de Google y Meta, ajustando presupuestos y segmentación para máximo ROI.',
    changePackageDesc: 'Sí, puedes actualizar o reducir tu paquete de mensajes en cualquier momento. Los cambios entran en vigor al inicio de tu próximo ciclo de facturación.',
    freeTrialDesc: '¡Sí! Ofrecemos una prueba gratuita de 14 días con hasta 100 mensajes incluidos. No se requiere tarjeta de crédito para comenzar.',
    readyToAutomateBusiness: '¿Listo para Automatizar tu Negocio?',
    joinThousandsBusinesses: 'Únete a miles de empresas en todo el mundo que han transformado su atención al cliente y ventas con BIZREP.',
    startFreeTrialBtn: 'Prueba Gratis'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    // Ciclo: en -> pt -> es -> en
    if (language === 'en') {
      setLanguage('pt');
    } else if (language === 'pt') {
      setLanguage('es');
    } else {
      setLanguage('en');
    }
  };

  const setSpecificLanguage = (lang) => {
    setLanguage(lang);
  };

  const t = (key) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, setSpecificLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
